import { useFlag, useUpdateUser } from '@reflag/react-sdk';
import { useAtom, useSetAtom } from 'jotai';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { UserTheme } from '@gitbook/app-database';
import type { NavigationScreenState } from '@gitbook/app-navigation';
import { isLegacySiteType } from '@gitbook/app-pricing';
import {
    createPalette,
    type PaletteCommandDef,
    PaletteCommandType,
    type PaletteGroupDef,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Switch, toast } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { appReloadingMessageAtom } from '../../Application';
import {
    getOrganizationPlanBadgeProps,
    OrganizationIcon,
    SiteContentIcon,
    SiteTypeBadge,
} from '../../Components';
import { useNavigation } from '../../navigation';
import {
    apiResource,
    readAPICache,
    siteCentricOptInAtom,
    useAuthOperations,
    useLoggedInUser,
    userThemeAtom,
} from '../../state';
import { transitionSiteCentric } from './SiteCentricTransition';

/**
 * Settings palette accessible from the sidebar header.
 */
export function useGetSettingsPalette(props: {
    organization: api.Organization;
    organizationContent: api.OrganizationAllContent;
    onCreateSite?: () => void;
    screen: NavigationScreenState;
}) {
    const { onCreateSite, organization, organizationContent, screen } = props;
    const { signOut } = useAuthOperations();
    const user = useLoggedInUser();
    const { navigate } = useNavigation();
    const [userTheme, setUserTheme] = useAtom(userThemeAtom);
    const setSiteCentricOptIn = useSetAtom(siteCentricOptInAtom);

    const isSiteCentricNavEnabled = useFlag('SITE_CENTRIC').isEnabled;
    const isSiteCentricToggleEnabled = useFlag('SITE_CENTRIC_TOGGLE').isEnabled;
    const updateUser = useUpdateUser();
    const setAppReloadingMessage = useSetAtom(appReloadingMessageAtom);

    const getOrganizationListPaletteGroups = useGetOrganizationListPaletteGroups();
    const getSiteListPaletteGroups = useGetSiteListPaletteGroups();
    return useEventCallback(() => {
        const groups: PaletteGroupDef[] = [];

        const switchTheme = (theme: UserTheme) => {
            setUserTheme(theme);
            analytics.logEvent({
                type: 'account_edit_theme',
                theme,
            });
        };

        const organizationSettingsCommands: PaletteCommandDef[] = [
            {
                key: 'org_settings.profile',
                title: 'Settings',
                icon: Icon.Settings,
                trigger: () => {
                    navigate({
                        screen: {
                            type: 'org_settings',
                            organization: organization.id,
                            tab: { type: 'profile' },
                        },
                    });
                },
            },
            ...(organization.permissions.admin
                ? [
                      {
                          key: 'org_settings.invite',
                          title: 'Invite your team',
                          icon: Icon.UserAdd,
                          trigger: () => {
                              navigate({
                                  overlay: {
                                      type: 'org_invite',
                                      organization: organization.id,
                                  },
                              });
                          },
                      },
                  ]
                : []),
        ];

        if (isSiteCentricNavEnabled) {
            const trackingContext = {
                type: 'click',
                action: 'open',
                organization: organization.id,
                screen: screen.type,
                screenSection: 'org_menu',
            } satisfies Omit<analytics.ClickAnalyticsEvent, 'component'>;

            groups.push({
                key: 'general',
                title: organization.title,
                commands: [
                    {
                        key: 'general.home',
                        title: 'Home',
                        icon: Icon.Home,
                        trigger: () => {
                            analytics.logEvent({
                                ...trackingContext,
                                component: 'home_link',
                            });
                            navigate({
                                screen: {
                                    type: 'home',
                                    organization: organization.id,
                                },
                            });
                        },
                    },
                    ...organizationSettingsCommands,
                ],
            });
            groups.push({
                key: 'organization_content',
                commands: [
                    {
                        key: 'general.sites',
                        title: 'Docs sites',
                        icon: Icon.DocsSite,
                        descendant: () =>
                            createPalette({
                                groups: getSiteListPaletteGroups({
                                    currentSiteId: 'site' in screen ? (screen.site ?? null) : null,
                                    organizationContent,
                                    organizationId: organization.id,
                                    onCreateSite,
                                    trackingContext,
                                }),
                            }),
                    },
                    {
                        key: 'general.content',
                        title: 'Content',
                        icon: Icon.AllSpaces,
                        trigger: () => {
                            navigate({
                                screen: {
                                    type: 'org_content',
                                    organization: organization.id,
                                },
                            });
                        },
                    },
                ],
            });
        }

        if (!isSiteCentricNavEnabled) {
            groups.push({
                key: 'org_settings',
                commands: organizationSettingsCommands,
            });
        }

        groups.push({
            key: 'personal_settings',
            commands: [
                {
                    key: 'personal_settings.switch',
                    icon: Icon.Modes,
                    title: 'Theme',
                    descendant: () =>
                        createPalette({
                            static: true,
                            groups: [
                                {
                                    key: 'theme',
                                    commands: [
                                        {
                                            key: 'theme.default',
                                            title: 'System',
                                            icon: Icon.DeviceDesktop,
                                            type: PaletteCommandType.RADIO,
                                            selected: userTheme === UserTheme.Default,
                                            trigger: () => switchTheme(UserTheme.Default),
                                        },
                                        {
                                            key: 'theme.light',
                                            title: 'Light',
                                            icon: Icon.ThemeLight,
                                            type: PaletteCommandType.RADIO,
                                            selected: userTheme === UserTheme.Light,
                                            trigger: () => switchTheme(UserTheme.Light),
                                        },
                                        {
                                            key: 'theme.dark',
                                            title: 'Dark',
                                            icon: Icon.ThemeDark,
                                            type: PaletteCommandType.RADIO,
                                            selected: userTheme === UserTheme.Dark,
                                            trigger: () => switchTheme(UserTheme.Dark),
                                        },
                                    ],
                                },
                            ],
                        }),
                },
            ],
        });

        groups.push({
            key: 'account',
            commands: [
                {
                    key: 'switch-organization',
                    icon: Icon.Organization,
                    title: 'Switch organization',
                    descendant: () =>
                        createPalette({
                            groups: getOrganizationListPaletteGroups({
                                organizationId: organization.id,
                            }),
                        }),
                },
                {
                    key: 'account.signout',
                    icon: Icon.Leave,
                    title: 'Sign out',
                    trigger: () => {
                        signOut();
                    },
                },
                ...(isSiteCentricToggleEnabled
                    ? [
                          {
                              key: 'site_centric_navigation.toggle',
                              title: 'Try our new navigation',
                              subtitle: 'Browse content the way your site is organized.',
                              truncate: false,
                              kind: 'outline',
                              size: 'large',
                              className: '[&_span.flex-1]:max-w-44 group/toggle-palette-command',
                              badge: { kind: 'new', label: 'NEW' },
                              hasIconSibling: false,
                              type: PaletteCommandType.CUSTOM,
                              enabled: isSiteCentricNavEnabled,
                              suffix: (
                                  <Switch
                                      className="pointer-events-none ml-2 shrink-0 group-hover/toggle-palette-command:[&:not([data-state])]:bg-neutral-ui-standout group-data-[focused]/toggle-palette-command:[&:not([data-state])]:bg-neutral-ui-standout"
                                      aria-label="Try our new navigation"
                                      value={isSiteCentricNavEnabled}
                                  />
                              ),
                              trigger: () => {
                                  void toast.catch(async () => {
                                      await transitionSiteCentric({
                                          navigate,
                                          organizationId: organization.id,
                                          reloadingMessage: isSiteCentricNavEnabled
                                              ? 'Restoring your previous experience'
                                              : 'Getting your new experience ready',
                                          setAppReloadingMessage,
                                          setSiteCentricOptIn,
                                          siteCentricOptIn: isSiteCentricNavEnabled
                                              ? 'false'
                                              : 'true',
                                          updateUser,
                                          onUpdated: () => {
                                              analytics.logEvent({
                                                  type: 'account_toggle_site_centric',
                                                  toggleStatusTo: !isSiteCentricNavEnabled,
                                                  userId: user.uid,
                                                  organization: organization.id,
                                              });
                                          },
                                      });
                                  }, 'Error while updating the new site-centric navigation');
                              },
                          } satisfies PaletteCommandDef,
                      ]
                    : []),
            ],
        });

        return createPalette({ groups });
    });
}

function useGetOrganizationListPaletteGroups() {
    const { navigate } = useNavigation();
    return useEventCallback((props: { organizationId: string }): PaletteGroupDef[] => {
        const { organizationId } = props;
        return [
            {
                key: 'organizations',
                commands: () => {
                    const organizations = readAPICache(
                        apiResource('listOrganizationsForAuthenticatedUser', { limit: 100 })
                    );

                    return [
                        ...organizations.items.map((org) => {
                            return {
                                key: org.id,
                                title: org.title,
                                ...(org.id === organizationId
                                    ? {
                                          type: PaletteCommandType.RADIO as const,
                                          selected: true,
                                      }
                                    : {
                                          selected: undefined,
                                      }),
                                icon: <OrganizationIcon organization={org} />,
                                badge:
                                    getOrganizationPlanBadgeProps({
                                        plan: org.plan,
                                        trialing:
                                            org.trial.status === api.BillingTrialStatus.Active,
                                        blend: true,
                                    }) ?? undefined,
                                trigger: () => {
                                    navigate({
                                        screen: {
                                            type: 'home',
                                            organization: org.id,
                                        },
                                    });
                                },
                            };
                        }),
                        {
                            key: 'create-organization',
                            title: 'Create an organization',
                            icon: Icon.Organization,
                            trigger: () => {
                                analytics.logEvent({
                                    type: 'organization_open_create',
                                    reason: 'sidebar_menu',
                                });

                                navigate({
                                    screen: {
                                        type: 'welcome',
                                        hash: 'sidebar_menu',
                                    },
                                });
                            },
                        },
                    ];
                },
            },
        ];
    });
}

export function useGetSiteListPaletteGroups() {
    const { navigate } = useNavigation();
    return useEventCallback(
        (props: {
            currentSiteId: string | null;
            organizationContent: api.OrganizationAllContent;
            organizationId: string;
            onCreateSite?: () => void;
            trackingContext: Omit<analytics.ClickAnalyticsEvent, 'component'>;
        }): PaletteGroupDef[] => {
            const {
                currentSiteId,
                onCreateSite,
                organizationContent,
                organizationId,
                trackingContext,
            } = props;
            return [
                {
                    key: 'sites',
                    commands: organizationContent.sites.map((entry) => {
                        const { site } = entry;
                        return {
                            key: site.id,
                            title: site.title,
                            ...(site.id === currentSiteId
                                ? {
                                      type: PaletteCommandType.RADIO as const,
                                      selected: true,
                                  }
                                : {
                                      selected: undefined,
                                  }),
                            icon: <SiteContentIcon site={site} />,
                            suffix: !isLegacySiteType(site.appliedType) ? (
                                <SiteTypeBadge
                                    type={site.type}
                                    appliedType={site.appliedType}
                                    size="xxsmall"
                                    weight="medium"
                                />
                            ) : undefined,
                            trigger: () => {
                                if (site.id !== currentSiteId) {
                                    analytics.logEvent({
                                        ...trackingContext,
                                        site: site.id,
                                        component: 'site_item',
                                    });
                                }
                                navigate({
                                    screen: {
                                        type: 'site',
                                        organization: organizationId,
                                        site: site.id,
                                    },
                                });
                            },
                        };
                    }),
                },
                ...(onCreateSite
                    ? [
                          {
                              key: 'create-site',
                              commands: [
                                  {
                                      key: 'create-site',
                                      title: 'Create a site',
                                      icon: Icon.Plus,
                                      trigger: () => {
                                          analytics.logEvent({
                                              ...trackingContext,
                                              component: 'create_site_item',
                                          });
                                          onCreateSite();
                                      },
                                  },
                              ],
                          },
                      ]
                    : []),
            ];
        }
    );
}
